import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Lead } from '@/types';
import { getLegacyCustomerName } from '@/types';
import type {
  IncomeSettings,
  UserIncomeConfig,
  MonthlyAdditionalIncome,
  LeadIncomeBreakdown,
  MonthlyIncomeSummary,
  BusinessHeadMonthlySummary,
  EmploymentType,
} from '@/types/income';

// ============================================================================
// COLLECTION REFERENCES
// ============================================================================

const incomeSettingsCollection = collection(db, 'income_settings');
const userIncomeConfigsCollection = collection(db, 'user_income_configs');
const additionalIncomesCollection = collection(db, 'monthly_additional_incomes');
const leadIncomeBreakdownsCollection = collection(db, 'lead_income_breakdowns');
const monthlyIncomeSummariesCollection = collection(db, 'monthly_income_summaries');
const bhMonthlySummariesCollection = collection(db, 'bh_monthly_summaries');

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Convert Firestore timestamp to Date
 */
const convertTimestamp = (timestamp: Date | Timestamp | undefined): Date => {
  if (timestamp instanceof Date) return timestamp;
  if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
    return timestamp.toDate();
  }
  return new Date();
};

/**
 * Remove undefined fields from object (Firestore doesn't accept undefined)
 */
const removeUndefinedFields = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const cleaned: Partial<T> = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value !== undefined) {
      cleaned[key as keyof T] = value;
    }
  });
  return cleaned;
};

/**
 * Get month string from date (YYYY-MM format)
 */
export const getMonthString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * Parse month string to Date (first day of month)
 */
export const parseMonthString = (monthStr: string): Date => {
  const [year, month] = monthStr.split('-');
  return new Date(parseInt(year), parseInt(month) - 1, 1);
};

// ============================================================================
// INCOME SETTINGS
// ============================================================================

/**
 * Get global income settings
 */
export const getIncomeSettings = async (): Promise<IncomeSettings> => {
  try {
    const docRef = doc(incomeSettingsCollection, 'global_settings');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        ...data,
        updatedAt: convertTimestamp(data.updatedAt),
      } as IncomeSettings;
    }
    
    // Return default settings if not found
    return {
      id: 'global_settings',
      businessHeadPayoutPercentage: 92,
      companyPayoutPercentage: 8,
      updatedAt: new Date(),
      updatedBy: 'system',
      updatedByName: 'System',
    };
  } catch (error) {
    console.error('Error fetching income settings:', error);
    throw error;
  }
};

/**
 * Update income settings
 */
export const updateIncomeSettings = async (
  updates: Partial<Omit<IncomeSettings, 'id' | 'updatedAt'>>,
  userId: string,
  userName: string
): Promise<void> => {
  try {
    const docRef = doc(incomeSettingsCollection, 'global_settings');
    const now = Timestamp.now();
    
    // Auto-calculate company percentage
    let updateData: any = {
      ...updates,
      updatedAt: now,
      updatedBy: userId,
      updatedByName: userName,
    };
    
    if (updates.businessHeadPayoutPercentage !== undefined) {
      updateData.companyPayoutPercentage = 100 - updates.businessHeadPayoutPercentage;
    }
    
    await setDoc(docRef, removeUndefinedFields(updateData), { merge: true });
  } catch (error) {
    console.error('Error updating income settings:', error);
    throw error;
  }
};

// ============================================================================
// USER INCOME CONFIGURATION
// ============================================================================

/**
 * Get all user income configurations
 */
export const getAllUserIncomeConfigs = async (): Promise<UserIncomeConfig[]> => {
  try {
    const snapshot = await getDocs(userIncomeConfigsCollection);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
      } as UserIncomeConfig;
    });
  } catch (error) {
    console.error('Error fetching user income configs:', error);
    return [];
  }
};

/**
 * Get user income configuration
 */
export const getUserIncomeConfig = async (userId: string): Promise<UserIncomeConfig | null> => {
  try {
    const docRef = doc(userIncomeConfigsCollection, userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        ...data,
        id: docSnap.id,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
      } as UserIncomeConfig;
    }
    return null;
  } catch (error) {
    console.error(`Error fetching user income config for ${userId}:`, error);
    return null;
  }
};

/**
 * Create or update user income configuration
 */
export const saveUserIncomeConfig = async (
  config: Omit<UserIncomeConfig, 'createdAt' | 'updatedAt'>,
  updatedBy: string,
  updatedByName: string
): Promise<void> => {
  try {
    const docRef = doc(userIncomeConfigsCollection, config.userId);
    const now = Timestamp.now();
    
    const existing = await getUserIncomeConfig(config.userId);
    
    const configData: any = {
      ...config,
      updatedAt: now,
      updatedBy,
      updatedByName,
    };
    
    if (!existing) {
      configData.createdAt = now;
    }
    
    await setDoc(docRef, removeUndefinedFields(configData), { merge: true });
  } catch (error) {
    console.error('Error saving user income config:', error);
    throw error;
  }
};

// ============================================================================
// MONTHLY ADDITIONAL INCOME
// ============================================================================

/**
 * Get additional incomes for a specific month
 */
export const getAdditionalIncomesForMonth = async (
  month: string
): Promise<MonthlyAdditionalIncome[]> => {
  try {
    const q = query(
      additionalIncomesCollection,
      where('month', '==', month),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
      } as MonthlyAdditionalIncome;
    });
  } catch (error) {
    console.error(`Error fetching additional incomes for ${month}:`, error);
    return [];
  }
};

/**
 * Get additional income for a user in a specific month
 */
export const getAdditionalIncomeForUser = async (
  userId: string,
  month: string
): Promise<MonthlyAdditionalIncome | null> => {
  try {
    const q = query(
      additionalIncomesCollection,
      where('userId', '==', userId),
      where('month', '==', month)
    );
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      const data = snapshot.docs[0].data();
      return {
        ...data,
        id: snapshot.docs[0].id,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
      } as MonthlyAdditionalIncome;
    }
    return null;
  } catch (error) {
    console.error(`Error fetching additional income for ${userId} in ${month}:`, error);
    return null;
  }
};

/**
 * Save additional income (create or update)
 */
export const saveAdditionalIncome = async (
  userId: string,
  userName: string,
  month: string,
  amount: number,
  reason: string,
  createdBy: string,
  createdByName: string
): Promise<void> => {
  try {
    const existing = await getAdditionalIncomeForUser(userId, month);
    const now = Timestamp.now();
    
    if (existing) {
      // Update existing
      const docRef = doc(additionalIncomesCollection, existing.id);
      await updateDoc(docRef, removeUndefinedFields({
        amount,
        reason,
        updatedAt: now,
      }));
    } else {
      // Create new
      const docRef = doc(additionalIncomesCollection);
      await setDoc(docRef, removeUndefinedFields({
        userId,
        userName,
        month,
        amount,
        reason,
        createdBy,
        createdByName,
        createdAt: now,
      }));
    }
  } catch (error) {
    console.error('Error saving additional income:', error);
    throw error;
  }
};

// ============================================================================
// LEAD INCOME BREAKDOWN CALCULATION
// ============================================================================

/**
 * Calculate income breakdown for a single lead (CORRECTED LOGIC)
 */
export const calculateLeadIncomeBreakdown = async (
  lead: Lead,
  userConfig: UserIncomeConfig,
  incomeSettings: IncomeSettings
): Promise<Omit<LeadIncomeBreakdown, 'id' | 'calculatedAt' | 'calculatedBy'>> => {
  
  const disbursedAmount = lead.disbursedAmount || 0;
  const bankPayoutPercentage = lead.payoutPercentage || 0; // e.g., 1%
  const vendorPayoutPercentage = lead.vendorPayoutPercentage || 0; // e.g., 0.5%
  
  // Step 1: Calculate total bank payout
  const totalBankPayout = (disbursedAmount * bankPayoutPercentage) / 100;
  
  // Step 2: Calculate company income (8% of total)
  const companyIncome = (totalBankPayout * incomeSettings.companyPayoutPercentage) / 100;
  
  // Step 3: Calculate BH gross amount (92% of total)
  const businessHeadGrossAmount = (totalBankPayout * incomeSettings.businessHeadPayoutPercentage) / 100;
  
  // Step 4: Calculate vendor payout (from disbursed amount)
  const vendorPayoutAmount = (disbursedAmount * vendorPayoutPercentage) / 100;
  
  // Step 5: Calculate net after vendor
  const netAfterVendor = businessHeadGrossAmount - vendorPayoutAmount;
  
  let finalEmployeeIncome = 0;
  let finalBusinessHeadIncome = 0;
  let freelanceGrossIncome: number | undefined;
  let freelanceNetIncome: number | undefined;
  let freelanceBHShare: number | undefined;
  let employeeRevenueShare: number | undefined;
  let businessHeadRevenueShare: number | undefined;
  
  if (userConfig.employmentType === 'freelance') {
    // CORRECTED FREELANCE CALCULATION
    // Freelancer gets X% of total bank payout (e.g., 85% of 1%)
    const freelancePercentage = userConfig.freelancePayoutPercentage || 0;
    freelanceGrossIncome = (totalBankPayout * freelancePercentage) / 100;
    
    // Freelancer pays vendor from their share
    freelanceNetIncome = freelanceGrossIncome - vendorPayoutAmount;
    
    // BH gets the remaining percentage (92% - 85% = 7% of total)
    const bhPercentage = incomeSettings.businessHeadPayoutPercentage - freelancePercentage;
    freelanceBHShare = (totalBankPayout * bhPercentage) / 100;
    
    finalEmployeeIncome = freelanceNetIncome;
    finalBusinessHeadIncome = freelanceBHShare;
    
  } else if (userConfig.employmentType === 'salaried') {
    // SALARIED CALCULATION
    // Split net after vendor 50-50
    employeeRevenueShare = netAfterVendor / 2;
    businessHeadRevenueShare = netAfterVendor / 2;
    
    // For salaried, actual income is calculated monthly (not per lead)
    // This is just their revenue contribution
    finalEmployeeIncome = 0; // Will be calculated in monthly summary
    finalBusinessHeadIncome = businessHeadRevenueShare;
  }
  
  const disbursementDate = lead.salesConsiderationDate || new Date();
  
  return {
    leadId: lead.id,
    leadName: getLegacyCustomerName(lead),
    loanType: lead.loanType!,
    lender: lead.lender || 'Unknown',
    
    disbursedAmount,
    bankPayoutPercentage,
    totalBankPayout,
    
    companyPayoutPercentage: incomeSettings.companyPayoutPercentage,
    companyIncome,
    
    businessHeadPayoutPercentage: incomeSettings.businessHeadPayoutPercentage,
    businessHeadGrossAmount,
    
    vendorPayoutPercentage,
    vendorPayoutAmount,
    
    netAfterVendor,
    
    assignedUserId: lead.createdBy || lead.assignedTo,
    assignedUserName: userConfig.userName,
    userEmploymentType: userConfig.employmentType,
    
    freelancePayoutPercentage: userConfig.freelancePayoutPercentage,
    freelanceGrossIncome,
    freelanceNetIncome,
    freelanceBHShare,
    
    employeeRevenueShare,
    businessHeadRevenueShare,
    
    finalEmployeeIncome,
    finalBusinessHeadIncome,
    finalCompanyIncome: companyIncome,
    
    disbursementMonth: getMonthString(disbursementDate),
    disbursementDate,
    
    isManuallyAdjusted: false,
  };
};

/**
 * Save lead income breakdown
 */
export const saveLeadIncomeBreakdown = async (
  breakdown: Omit<LeadIncomeBreakdown, 'id' | 'calculatedAt' | 'calculatedBy'>,
  calculatedBy: string
): Promise<void> => {
  try {
    const docRef = doc(leadIncomeBreakdownsCollection, breakdown.leadId);
    const now = Timestamp.now();
    
    await setDoc(docRef, removeUndefinedFields({
      ...breakdown,
      disbursementDate: breakdown.disbursementDate instanceof Date
        ? Timestamp.fromDate(breakdown.disbursementDate)
        : breakdown.disbursementDate,
      calculatedAt: now,
      calculatedBy,
    }));
  } catch (error) {
    console.error('Error saving lead income breakdown:', error);
    throw error;
  }
};

/**
 * Get lead income breakdown
 */
export const getLeadIncomeBreakdown = async (leadId: string): Promise<LeadIncomeBreakdown | null> => {
  try {
    const docRef = doc(leadIncomeBreakdownsCollection, leadId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        ...data,
        id: docSnap.id,
        disbursementDate: convertTimestamp(data.disbursementDate),
        calculatedAt: convertTimestamp(data.calculatedAt),
        adjustedAt: convertTimestamp(data.adjustedAt),
      } as LeadIncomeBreakdown;
    }
    return null;
  } catch (error) {
    console.error(`Error fetching lead income breakdown for ${leadId}:`, error);
    return null;
  }
};

/**
 * Get all lead income breakdowns for a specific month
 */
export const getLeadIncomeBreakdownsForMonth = async (
  month: string
): Promise<LeadIncomeBreakdown[]> => {
  try {
    const q = query(
      leadIncomeBreakdownsCollection,
      where('disbursementMonth', '==', month)
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        disbursementDate: convertTimestamp(data.disbursementDate),
        calculatedAt: convertTimestamp(data.calculatedAt),
        adjustedAt: convertTimestamp(data.adjustedAt),
      } as LeadIncomeBreakdown;
    });
  } catch (error) {
    console.error(`Error fetching lead income breakdowns for ${month}:`, error);
    return [];
  }
};

/**
 * Get lead income breakdowns for a specific user and month
 */
export const getLeadIncomeBreakdownsForUser = async (
  userId: string,
  month: string
): Promise<LeadIncomeBreakdown[]> => {
  try {
    const q = query(
      leadIncomeBreakdownsCollection,
      where('assignedUserId', '==', userId),
      where('disbursementMonth', '==', month)
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        disbursementDate: convertTimestamp(data.disbursementDate),
        calculatedAt: convertTimestamp(data.calculatedAt),
        adjustedAt: convertTimestamp(data.adjustedAt),
      } as LeadIncomeBreakdown;
    });
  } catch (error) {
    console.error(`Error fetching lead income breakdowns for user ${userId} in ${month}:`, error);
    return [];
  }
};

/**
 * Manually adjust lead income breakdown
 */
export const updateLeadIncomeBreakdown = async (
  leadId: string,
  updates: Partial<LeadIncomeBreakdown>,
  adjustedBy: string,
  adjustmentReason: string
): Promise<void> => {
  try {
    const docRef = doc(leadIncomeBreakdownsCollection, leadId);
    const now = Timestamp.now();
    
    await updateDoc(docRef, removeUndefinedFields({
      ...updates,
      isManuallyAdjusted: true,
      adjustmentReason,
      adjustedBy,
      adjustedAt: now,
    }));
  } catch (error) {
    console.error('Error updating lead income breakdown:', error);
    throw error;
  }
};

// ============================================================================
// MONTHLY INCOME SUMMARY CALCULATION
// ============================================================================

/**
 * Calculate monthly income summary for a user
 */
export const calculateMonthlyIncomeSummary = async (
  userId: string,
  month: string,
  userConfig: UserIncomeConfig,
  leadBreakdowns: LeadIncomeBreakdown[],
  additionalIncome?: MonthlyAdditionalIncome
): Promise<Omit<MonthlyIncomeSummary, 'id' | 'calculatedAt' | 'calculatedBy'>> => {
  
  const additionalAmount = additionalIncome?.amount || 0;
  const totalLeads = leadBreakdowns.length;
  const totalDisbursementAmount = leadBreakdowns.reduce((sum, l) => sum + l.disbursedAmount, 0);
  const totalVendorPayout = leadBreakdowns.reduce((sum, l) => sum + l.vendorPayoutAmount, 0);
  const leadIds = leadBreakdowns.map(l => l.leadId);
  
  let baseIncome = 0;
  let incentiveIncome = 0;
  let totalRevenueGenerated = 0;
  let totalIncentiveEligible = 0;
  let isSalaryJustified = true;
  let salaryShortfall: number | undefined;
  let salaryExcess: number | undefined;
  
  if (userConfig.employmentType === 'freelance') {
    // Freelance: Sum all freelanceNetIncome
    const totalFreelanceIncome = leadBreakdowns.reduce(
      (sum, l) => sum + (l.freelanceNetIncome || 0),
      0
    );
    
    baseIncome = 0;
    incentiveIncome = totalFreelanceIncome;
    isSalaryJustified = true; // N/A for freelancers
    
  } else if (userConfig.employmentType === 'salaried') {
    // Salaried: Calculate salary justification
    const salary = userConfig.monthlySalary || 0;
    
    totalRevenueGenerated = leadBreakdowns.reduce(
      (sum, l) => sum + (l.employeeRevenueShare || 0),
      0
    );
    
    // For salaried, incentive eligible is the revenue share (already ÷ 2)
    totalIncentiveEligible = totalRevenueGenerated;
    
    baseIncome = salary;
    
    if (totalIncentiveEligible >= salary) {
      // Salary justified + bonus
      incentiveIncome = totalIncentiveEligible - salary;
      salaryExcess = incentiveIncome;
      isSalaryJustified = true;
    } else {
      // Shortfall covered by BH
      salaryShortfall = salary - totalIncentiveEligible;
      incentiveIncome = 0;
      isSalaryJustified = false;
    }
  }
  
  const totalIncome = baseIncome + incentiveIncome + additionalAmount;
  
  return {
    userId,
    userName: userConfig.userName,
    month,
    employmentType: userConfig.employmentType,
    
    monthlySalary: userConfig.monthlySalary,
    totalRevenueGenerated,
    totalIncentiveEligible,
    
    baseIncome,
    incentiveIncome,
    additionalIncome: additionalAmount,
    totalIncome,
    
    isSalaryJustified,
    salaryShortfall,
    salaryExcess,
    
    totalLeads,
    totalDisbursementAmount,
    totalVendorPayout,
    
    leadIds,
  };
};

/**
 * Save monthly income summary
 */
export const saveMonthlyIncomeSummary = async (
  summary: Omit<MonthlyIncomeSummary, 'id' | 'calculatedAt' | 'calculatedBy'>,
  calculatedBy: string
): Promise<void> => {
  try {
    const docId = `${summary.userId}_${summary.month}`;
    const docRef = doc(monthlyIncomeSummariesCollection, docId);
    const now = Timestamp.now();
    
    await setDoc(docRef, removeUndefinedFields({
      ...summary,
      calculatedAt: now,
      calculatedBy,
    }));
  } catch (error) {
    console.error('Error saving monthly income summary:', error);
    throw error;
  }
};

/**
 * Get monthly income summary for a user
 */
export const getMonthlyIncomeSummary = async (
  userId: string,
  month: string
): Promise<MonthlyIncomeSummary | null> => {
  try {
    const docId = `${userId}_${month}`;
    const docRef = doc(monthlyIncomeSummariesCollection, docId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        ...data,
        id: docSnap.id,
        calculatedAt: convertTimestamp(data.calculatedAt),
      } as MonthlyIncomeSummary;
    }
    return null;
  } catch (error) {
    console.error(`Error fetching monthly income summary for ${userId} in ${month}:`, error);
    return null;
  }
};

/**
 * Get all monthly income summaries for a specific month
 */
export const getAllMonthlyIncomeSummariesForMonth = async (
  month: string
): Promise<MonthlyIncomeSummary[]> => {
  try {
    const q = query(
      monthlyIncomeSummariesCollection,
      where('month', '==', month)
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        calculatedAt: convertTimestamp(data.calculatedAt),
      } as MonthlyIncomeSummary;
    });
  } catch (error) {
    console.error(`Error fetching monthly income summaries for ${month}:`, error);
    return [];
  }
};

// ============================================================================
// BUSINESS HEAD MONTHLY SUMMARY
// ============================================================================

/**
 * Calculate Business Head monthly summary
 */
export const calculateBusinessHeadMonthlySummary = async (
  month: string,
  leadBreakdowns: LeadIncomeBreakdown[],
  monthlySummaries: MonthlyIncomeSummary[]
): Promise<Omit<BusinessHeadMonthlySummary, 'id' | 'calculatedAt' | 'calculatedBy'>> => {
  
  const totalCompanyRevenue = leadBreakdowns.reduce((sum, l) => sum + l.totalBankPayout, 0);
  const totalCompanyIncome = leadBreakdowns.reduce((sum, l) => sum + l.finalCompanyIncome, 0);
  const totalBusinessHeadGross = leadBreakdowns.reduce((sum, l) => sum + l.businessHeadGrossAmount, 0);
  const totalVendorPayout = leadBreakdowns.reduce((sum, l) => sum + l.vendorPayoutAmount, 0);
  
  const directBHIncome = leadBreakdowns.reduce((sum, l) => sum + l.finalBusinessHeadIncome, 0);
  
  const salaryShortfallCovered = monthlySummaries.reduce(
    (sum, s) => sum + (s.salaryShortfall || 0),
    0
  );
  
  const finalBHIncome = directBHIncome - salaryShortfallCovered;
  
  const totalLeads = leadBreakdowns.length;
  const totalDisbursementAmount = leadBreakdowns.reduce((sum, l) => sum + l.disbursedAmount, 0);
  const totalEmployeesPaid = monthlySummaries.length;
  const salariedEmployees = monthlySummaries.filter(s => s.employmentType === 'salaried').length;
  const freelanceEmployees = monthlySummaries.filter(s => s.employmentType === 'freelance').length;
  
  return {
    month,
    totalCompanyRevenue,
    totalCompanyIncome,
    totalBusinessHeadGross,
    totalVendorPayout,
    directBHIncome,
    salaryShortfallCovered,
    finalBHIncome,
    totalLeads,
    totalDisbursementAmount,
    totalEmployeesPaid,
    salariedEmployees,
    freelanceEmployees,
  };
};

/**
 * Save Business Head monthly summary
 */
export const saveBusinessHeadMonthlySummary = async (
  summary: Omit<BusinessHeadMonthlySummary, 'id' | 'calculatedAt' | 'calculatedBy'>,
  calculatedBy: string
): Promise<void> => {
  try {
    const docId = `BH_${summary.month}`;
    const docRef = doc(bhMonthlySummariesCollection, docId);
    const now = Timestamp.now();
    
    await setDoc(docRef, removeUndefinedFields({
      ...summary,
      calculatedAt: now,
      calculatedBy,
    }));
  } catch (error) {
    console.error('Error saving BH monthly summary:', error);
    throw error;
  }
};

/**
 * Get Business Head monthly summary
 */
export const getBusinessHeadMonthlySummary = async (
  month: string
): Promise<BusinessHeadMonthlySummary | null> => {
  try {
    const docId = `BH_${month}`;
    const docRef = doc(bhMonthlySummariesCollection, docId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        ...data,
        id: docSnap.id,
        calculatedAt: convertTimestamp(data.calculatedAt),
      } as BusinessHeadMonthlySummary;
    }
    return null;
  } catch (error) {
    console.error(`Error fetching BH monthly summary for ${month}:`, error);
    return null;
  }
};

// ============================================================================
// BATCH CALCULATION FOR MONTH
// ============================================================================

/**
 * Calculate all income for a specific month (batch operation)
 * This should be run monthly or when recalculation is needed
 */
export const calculateMonthIncomes = async (
  month: string,
  leads: Lead[],
  calculatedBy: string
): Promise<void> => {
  try {
    const incomeSettings = await getIncomeSettings();
    const userConfigs = await getAllUserIncomeConfigs();
    const additionalIncomes = await getAdditionalIncomesForMonth(month);
    
    // Step 1: Calculate and save lead income breakdowns
    const leadBreakdowns: LeadIncomeBreakdown[] = [];
    
    for (const lead of leads) {
      const userId = lead.createdBy || lead.assignedTo;
      const userConfig = userConfigs.find(c => c.userId === userId);
      
      if (!userConfig) {
        console.warn(`No income config found for user ${userId}, skipping lead ${lead.id}`);
        continue;
      }
      
      const breakdown = await calculateLeadIncomeBreakdown(lead, userConfig, incomeSettings);
      await saveLeadIncomeBreakdown(breakdown, calculatedBy);
      
      leadBreakdowns.push({
        ...breakdown,
        id: lead.id,
        calculatedAt: new Date(),
        calculatedBy,
      } as LeadIncomeBreakdown);
    }
    
    // Step 2: Calculate and save monthly summaries for each user
    const monthlySummaries: MonthlyIncomeSummary[] = [];
    
    for (const userConfig of userConfigs) {
      if (!userConfig.isActive) continue;
      
      const userLeadBreakdowns = leadBreakdowns.filter(
        l => l.assignedUserId === userConfig.userId
      );
      
      const additionalIncome = additionalIncomes.find(
        a => a.userId === userConfig.userId
      );
      
      const summary = await calculateMonthlyIncomeSummary(
        userConfig.userId,
        month,
        userConfig,
        userLeadBreakdowns,
        additionalIncome
      );
      
      await saveMonthlyIncomeSummary(summary, calculatedBy);
      
      monthlySummaries.push({
        ...summary,
        id: `${userConfig.userId}_${month}`,
        calculatedAt: new Date(),
        calculatedBy,
      } as MonthlyIncomeSummary);
    }
    
    // Step 3: Calculate and save BH monthly summary
    const bhSummary = await calculateBusinessHeadMonthlySummary(
      month,
      leadBreakdowns,
      monthlySummaries
    );
    
    await saveBusinessHeadMonthlySummary(bhSummary, calculatedBy);
    
  } catch (error) {
    console.error(`Error calculating month incomes for ${month}:`, error);
    throw error;
  }
};
